import {
  ArrowDownIcon,
  ArrowsClockwiseIcon,
  ArrowUpIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  DownloadIcon,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, formatPercentage } from '@/utils';

interface ValuationItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  quantity: number;
  unitCost: number;
  unitPrice: number;
  totalCostValue: number;
  totalRetailValue: number;
  margin: number;
  marginPercentage: number;
  turnoverRate: number;
  daysInStock: number;
  lastMovement: string;
  valuationMethod: 'FIFO' | 'LIFO' | 'Weighted Average';
}

interface ValuationSummary {
  totalCostValue: number;
  totalRetailValue: number;
  totalMargin: number;
  averageMarginPercentage: number;
  totalItems: number;
  averageTurnoverRate: number;
  slowMovingItems: number;
  deadStock: number;
  valuationDate: string;
}

interface CategoryBreakdown {
  category: string;
  itemCount: number;
  costValue: number;
  retailValue: number;
  margin: number;
  marginPercentage: number;
  percentage: number;
}

interface ValuationReportData {
  items: ValuationItem[];
  summary: ValuationSummary;
  categoryBreakdown: CategoryBreakdown[];
  trendData: {
    period: string;
    totalValue: number;
    change: number;
    changePercentage: number;
  }[];
  methodology: string;
  lastUpdated: string;
}

interface ValuationReportProps {
  method?: 'FIFO' | 'LIFO' | 'Weighted Average';
  className?: string;
}

const ValuationReport: React.FC<ValuationReportProps> = ({
  method = 'Weighted Average',
  className = '',
}) => {
  const [data, setData] = useState<ValuationReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<
    'summary' | 'items' | 'categories'
  >('summary');
  const [selectedMethod, setSelectedMethod] = useState(method);
  const [exporting, setExporting] = useState(false);

  const api = useEnhancedApi();
  const { toast } = useToast();
  const showToast = (message: string, success: boolean = true) => {
    toast({
      title: success ? 'Success' : 'Error',
      description: message,
      variant: success ? 'default' : 'destructive',
    });
  };

  useEffect(() => {
    fetchValuationReport();
  }, [selectedMethod]);

  const fetchValuationReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.inventory.getValuationReport({
        method: selectedMethod,
      });
      setData(response.data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch valuation report';
      setError(errorMessage);
      showToast(errorMessage, false);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      // TODO: Implement export functionality when API method is available
      // await api.inventory.exportData({
      //   type: 'valuation',
      //   format: 'excel',
      //   method: selectedMethod,
      // });
      showToast('Export functionality will be available soon');
    } catch (err) {
      showToast('Export failed. Please try again.', false);
    } finally {
      setExporting(false);
    }
  };

  const getTurnoverColor = (rate: number) => {
    if (rate >= 4) return 'text-success-60';
    if (rate >= 2) return 'text-warning-60';
    return 'text-destructive-60';
  };

  const getTurnoverBadge = (rate: number) => {
    if (rate >= 4)
      return {
        color: 'text-success-60 bg-success-5 border-success-20',
        label: 'Fast Moving',
      };
    if (rate >= 2)
      return {
        color: 'text-warning-60 bg-warning-5 border-warning-20',
        label: 'Moderate',
      };
    if (rate > 0)
      return {
        color: 'text-destructive-60 bg-destructive-5 border-destructive-20',
        label: 'Slow Moving',
      };
    return {
      color: 'text-gray-60 bg-gray-5 border-gray-20',
      label: 'Dead Stock',
    };
  };

  const handleRefresh = () => {
    fetchValuationReport();
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <CurrencyDollarIcon className='h-5 w-5' />
            <Skeleton className='h-6 w-40' />
          </div>
          <Skeleton className='h-4 w-80' />
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex gap-4'>
            <Skeleton className='h-10 w-40' />
            <Skeleton className='h-10 w-32' />
          </div>
          <div className='grid grid-cols-4 gap-4'>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className='h-24' />
            ))}
          </div>
          <Skeleton className='h-64 w-full' />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className='pt-6'>
          <Alert variant='destructive'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <CurrencyDollarIcon className='h-5 w-5 text-brand-60' />
            <div>
              <CardTitle>Inventory Valuation Report</CardTitle>
              <CardDescription>
                Comprehensive financial analysis of inventory holdings
              </CardDescription>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Select
              value={selectedMethod}
              onValueChange={(value: 'FIFO' | 'LIFO' | 'Weighted Average') =>
                setSelectedMethod(value)
              }
            >
              <SelectTrigger className='w-44'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='FIFO'>FIFO Method</SelectItem>
                <SelectItem value='LIFO'>LIFO Method</SelectItem>
                <SelectItem value='Weighted Average'>
                  Weighted Average
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              hierarchy='outlined'
              size='sm'
              onClick={handleRefresh}
              disabled={loading}
            >
              <ArrowsClockwiseIcon
                className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
              />
            </Button>
            <Button
              hierarchy='outlined'
              size='sm'
              onClick={handleExport}
              disabled={exporting}
            >
              <DownloadIcon className='h-4 w-4' />
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Summary Metrics */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
          <Card>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium text-gray-60'>
                  Total Cost Value
                </span>
              </div>
              <div className='text-2xl font-bold'>
                {formatCurrency(data.summary.totalCostValue)}
              </div>
              {data.trendData.length > 0 && (
                <div className='flex items-center gap-1 mt-1'>
                  {data.trendData[0].changePercentage >= 0 ? (
                    <ArrowUpIcon className='h-3 w-3 text-success-60' />
                  ) : (
                    <ArrowDownIcon className='h-3 w-3 text-destructive-60' />
                  )}
                  <span
                    className={`text-xs ${data.trendData[0].changePercentage >= 0 ? 'text-success-60' : 'text-destructive-60'}`}
                  >
                    {formatPercentage(
                      Math.abs(data.trendData[0].changePercentage)
                    )}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium text-gray-60'>
                  Total Retail Value
                </span>
              </div>
              <div className='text-2xl font-bold text-success-60'>
                {formatCurrency(data.summary.totalRetailValue)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium text-gray-60'>
                  Total Margin
                </span>
              </div>
              <div className='text-2xl font-bold text-brand-60'>
                {formatCurrency(data.summary.totalMargin)}
              </div>
              <div className='text-sm text-gray-60'>
                {formatPercentage(data.summary.averageMarginPercentage)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium text-gray-60'>
                  Avg Turnover
                </span>
              </div>
              <div
                className={`text-2xl font-bold ${getTurnoverColor(data.summary.averageTurnoverRate)}`}
              >
                {data.summary.averageTurnoverRate.toFixed(1)}×
              </div>
              <div className='text-sm text-gray-60'>
                {data.summary.slowMovingItems} slow moving
              </div>
            </CardContent>
          </Card>
        </div>

        {/* View Tabs */}
        <Tabs
          value={activeView}
          onValueChange={value =>
            setActiveView(value as 'summary' | 'items' | 'categories')
          }
        >
          <TabsList className='grid w-full grid-cols-3'>
            <TabsTrigger value='summary'>Summary</TabsTrigger>
            <TabsTrigger value='categories'>By Category</TabsTrigger>
            <TabsTrigger value='items'>Item Details</TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value='summary' className='mt-4'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Key Metrics</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='flex justify-between'>
                    <span className='text-gray-60'>Total Items:</span>
                    <span className='font-medium'>
                      {data.summary.totalItems.toLocaleString()}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-60'>Dead Stock Items:</span>
                    <span className='font-medium text-destructive-60'>
                      {data.summary.deadStock}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-60'>Slow Moving Items:</span>
                    <span className='font-medium text-warning-60'>
                      {data.summary.slowMovingItems}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-60'>Valuation Method:</span>
                    <Badge variant='outline'>{data.methodology}</Badge>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-60'>Last Updated:</span>
                    <span className='text-sm'>{data.lastUpdated}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Trend Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    {data.trendData.map((trend, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between py-2'
                      >
                        <span className='text-sm font-medium'>
                          {trend.period}
                        </span>
                        <div className='flex items-center gap-2'>
                          <span className='font-medium'>
                            {formatCurrency(trend.totalValue)}
                          </span>
                          <div className='flex items-center gap-1'>
                            {trend.changePercentage >= 0 ? (
                              <ArrowUpIcon className='h-3 w-3 text-success-60' />
                            ) : (
                              <ArrowDownIcon className='h-3 w-3 text-destructive-60' />
                            )}
                            <span
                              className={`text-xs ${trend.changePercentage >= 0 ? 'text-success-60' : 'text-destructive-60'}`}
                            >
                              {formatPercentage(
                                Math.abs(trend.changePercentage)
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value='categories' className='mt-4'>
            <div className='space-y-3'>
              {data.categoryBreakdown.map(category => (
                <Card key={category.category}>
                  <CardContent className='p-4'>
                    <div className='flex items-center justify-between mb-3'>
                      <div className='flex items-center gap-3'>
                        <ChartBarIcon className='h-5 w-5 text-brand-60' />
                        <div>
                          <div className='font-medium'>{category.category}</div>
                          <div className='text-sm text-gray-60'>
                            {category.itemCount} items
                          </div>
                        </div>
                      </div>
                      <Badge variant='outline'>
                        {formatPercentage(category.percentage)}
                      </Badge>
                    </div>

                    <div className='grid grid-cols-3 gap-4 mb-3'>
                      <div>
                        <div className='text-lg font-semibold'>
                          {formatCurrency(category.costValue)}
                        </div>
                        <div className='text-xs text-gray-60'>Cost Value</div>
                      </div>
                      <div>
                        <div className='text-lg font-semibold text-success-60'>
                          {formatCurrency(category.retailValue)}
                        </div>
                        <div className='text-xs text-gray-60'>Retail Value</div>
                      </div>
                      <div>
                        <div className='text-lg font-semibold text-brand-60'>
                          {formatCurrency(category.margin)}
                        </div>
                        <div className='text-xs text-gray-60'>
                          {formatPercentage(category.marginPercentage)} margin
                        </div>
                      </div>
                    </div>

                    <Progress value={category.percentage} className='h-2' />
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value='items' className='mt-4'>
            <div className='space-y-2 max-h-96 overflow-y-auto'>
              {data.items.map(item => {
                const turnoverBadge = getTurnoverBadge(item.turnoverRate);

                return (
                  <Card key={item.id}>
                    <CardContent className='p-4'>
                      <div className='flex items-start justify-between mb-3'>
                        <div className='flex items-center gap-3'>
                          <div>
                            <div className='font-medium'>{item.name}</div>
                            <div className='text-sm text-gray-60'>
                              {item.sku}
                            </div>
                          </div>
                          <Badge className={turnoverBadge.color}>
                            {turnoverBadge.label}
                          </Badge>
                        </div>
                        <div className='text-right'>
                          <div className='text-sm text-gray-60'>
                            Method: {item.valuationMethod}
                          </div>
                        </div>
                      </div>

                      <div className='grid grid-cols-4 gap-4 text-sm'>
                        <div>
                          <div className='text-gray-60'>Quantity</div>
                          <div className='font-medium'>{item.quantity}</div>
                        </div>
                        <div>
                          <div className='text-gray-60'>Cost Value</div>
                          <div className='font-medium'>
                            {formatCurrency(item.totalCostValue)}
                          </div>
                        </div>
                        <div>
                          <div className='text-gray-60'>Retail Value</div>
                          <div className='font-medium text-success-60'>
                            {formatCurrency(item.totalRetailValue)}
                          </div>
                        </div>
                        <div>
                          <div className='text-gray-60'>Turnover Rate</div>
                          <div
                            className={`font-medium ${getTurnoverColor(item.turnoverRate)}`}
                          >
                            {item.turnoverRate.toFixed(1)}×
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ValuationReport;
